import * as m from '@staiter/i18n';
import { getLanguageTag } from '@staiter/i18n';
import {
  PROJECT_HEALTH_LABEL,
  PROJECT_STAGE_LABEL,
  type ProjectHealth,
  type ProjectStage,
} from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { AlertTriangle, BarChart3, ChevronDown, Clock, Coins, MoreHorizontal } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { Breadcrumbs, Skeleton } from '~/components/data';
import { PageHeader } from '~/components/data';
import { AuditTab } from '~/components/domain/project/AuditTab';
import { CancelProjectDialog } from '~/components/domain/project/CancelProjectDialog';
import { CloseoutBlockers } from '~/components/domain/project/CloseoutBlockers';
import { FinancialPanel } from '~/components/domain/project/FinancialPanel';
import { KeyboardShortcutsOverlay } from '~/components/domain/project/KeyboardShortcutsOverlay';
import { LinkagesPanel } from '~/components/domain/project/LinkagesPanel';
import { MilestonesPanel } from '~/components/domain/project/MilestonesPanel';
import { PendingApprovalBadge } from '~/components/domain/project/PendingApprovalBadge';
import { PlanShiftDialog } from '~/components/domain/project/PlanShiftDialog';
import { ProjectAIInsightCard } from '~/components/domain/project/ProjectAIInsightCard';
import { ProjectActivityCard } from '~/components/domain/project/ProjectActivityCard';
import { ProjectVisibilityBadge } from '~/components/domain/project/ProjectVisibilityBadge';
import { Tier2ApprovalDialog } from '~/components/domain/project/Tier2ApprovalDialog';
import { UncancelProjectDialog } from '~/components/domain/project/UncancelProjectDialog';
import { emitToast } from '~/components/feedback';
import { useToast } from '~/components/feedback';
import { Button, Dialog, DisabledWithReason, Select, Textarea } from '~/components/primitives';
import { openContextMenuFromButton } from '~/components/primitives/ContextMenu';
import { StatusBadge } from '~/components/status';
import { DuplicateProjectDialog } from '~/components/tasks/blueprints/DuplicateProjectDialog';
import { SaveAsTemplateDialog } from '~/components/tasks/blueprints/SaveAsTemplateDialog';
import { submitProjectAction } from '~/lib/api/project-actions';
import {
  type PlanShiftInput,
  cancelProject,
  fetchProject,
  fetchProjectApprovers,
  fetchProjectCancelPreview,
  fetchProjectCloseout,
  fetchProjectPauseImpact,
  fetchProjectStatusJournal,
  postProjectStatusUpdate,
  shiftProjectPlan,
  transitionProjectStage,
  uncancelProject,
} from '~/lib/api/projects';
import { formatDate, formatMoney, formatNumber } from '~/lib/format';
import { healthBadge, isTerminalOutcome, stageBadge } from '~/lib/projects/badge-tone';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import {
  allowedStages,
  forwardStage,
  isTerminalStage,
  stageActionLabel,
} from '~/lib/tasks/actions/project-registry';
import { useProjectActions } from '~/lib/tasks/actions/use-project-actions';
import { PERCENT_COMPLETE_METHOD_I18N } from '~/lib/tasks/i18n/labels';
import styles from './_auth.projects.$id.index.module.css';

type TabKey =
  | 'overview'
  | 'tasks'
  | 'gantt'
  | 'milestones'
  | 'financial'
  | 'links'
  | 'task_log'
  | 'status_journal'
  | 'audit';

const detailSearchSchema = z.object({
  tab: z
    .enum([
      'overview',
      'tasks',
      'gantt',
      'milestones',
      'financial',
      'links',
      'task_log',
      'status_journal',
      'audit',
    ])
    .optional()
    .catch(undefined),
  // Z-F7 — якорь на КОНКРЕТНУЮ запись журнала. Без него «Открыть в журнале»
  // приводило на вкладку из ста строк, где свою правку надо было искать глазами.
  audit: z.string().uuid().optional().catch(undefined),
  /**
   * F15a — какое действие открыть сразу при заходе. ПКМ-меню живёт на другом
   * экране и отрендерить диалог само не может: оно ведёт сюда с этим
   * параметром, карточка открывает СВОЙ диалог (тот же, что из её меню), и
   * сразу вычищает параметр — иначе перезагрузка страницы открывала бы диалог
   * заново, а «назад» возвращал бы в него же.
   */
  action: z
    // F5 (project-plan-change) — перенос срока приходит сюда тем же якорем, что и
    // отмена: флоу живёт на карточке вместе с превью и подписью, меню только ведёт.
    .enum(['stage', 'status', 'cancel', 'uncancel', 'duplicate', 'save_template', 'plan_shift'])
    .optional()
    .catch(undefined),
});
// S12 — без .strict(): неизвестные query-параметры стрипаются, а не роняют app.

/** G5 — Project detail with tabs.
 * FE-12 fix (2026-05-31): renamed file to .index.tsx so edit child route
 * is not shadowed (same as FE-294 для sales-orders). Route path stays
 * `/_auth/projects/$id/` (TanStack index leaf) — detail и edit становятся
 * sibling leaves вместо parent/child.
 */
export const Route = createFileRoute('/_auth/projects/$id/')({
  validateSearch: detailSearchSchema,
  component: ProjectDetailPage,
});

const HEALTH_DOT: Record<string, string> = {
  on_track: 'var(--color-success, #22c55e)',
  at_risk: 'var(--color-warning, #eab308)',
  overdue: 'var(--color-danger, #ef4444)',
  on_hold: 'var(--text-muted, #94a3b8)',
};

// FE-95: добавить icon hint к health для colorblind users (status = colour + icon + text).
const HEALTH_ICON: Record<string, string> = {
  on_track: '✓',
  at_risk: '▲',
  overdue: '◆',
  on_hold: '⏸',
};

/**
 * Rework дизайн-гейта D4 — счётчик задач склоняется по правилам языка.
 * `Intl.PluralRules` вместо «если 1» — иначе «3 задач» доживает до следующего
 * ревью, а в языках с другим числом форм правило пришлось бы писать заново.
 */
/**
 * F1 — подтверждение отправки заявки.
 *
 * Тон нейтральный: заявка отправлена — не «готово». Текст называет главное, что
 * человеку сейчас неочевидно: объект не изменился и ждёт чужого решения (§6).
 */
function emitApprovalRequestedToast(): void {
  emitToast({
    variant: 'info',
    title: m.approval_request_sent_title(),
    description: m.approval_request_sent_body(),
  });
}

function pluralTasks(count: number): string {
  const rule = new Intl.PluralRules(getLanguageTag()).select(count);
  if (rule === 'one') return m.project_tasks_total_one({ count });
  if (rule === 'few') return m.project_tasks_total_few({ count });
  return m.project_tasks_total_n({ count });
}

function ProjectDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const search = useSearch({ from: '/_auth/projects/$id/' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['project', id],
    queryFn: () => fetchProject(id),
    retry: (failureCount, err) => {
      // G5.7 — no retry on 404 — caller will redirect.
      const status = (err as { status?: number } | null)?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });
  const project = query.data;
  const activeTab = (search.tab ?? 'overview') as TabKey;
  // F2-full+F3 — вкладки задачи/гант/журнал работ = launcher на full-bleed workspace
  // /work-tasks/$id (§3.4a — workspace полноэкранный, не «иллюминатор» в content-page).
  // biome-ignore lint/correctness/useExhaustiveDependencies: redirect on tab change
  useEffect(() => {
    if (activeTab === 'tasks' || activeTab === 'gantt' || activeTab === 'task_log') {
      navigate({ to: '/work-tasks/$projectId', params: { projectId: id }, replace: true });
    }
  }, [activeTab, id]);
  // FE-N Pass 78: native `window.alert` запрещён (заметно блокирует UI, не Calm).
  // Используем useToast (есть везде в проекте), variant=error.
  const toast = useToast();

  // G5.7 → FE-N Pass 78: 404 redirect через toast + navigate (раньше блокирующий alert).
  useEffect(() => {
    if (!query.isError) return;
    const status = (query.error as { status?: number } | null)?.status;
    if (status === 404) {
      toast.push({ variant: 'error', title: m.project_not_found_redirect_toast() });
      navigate({ to: '/projects' });
    }
  }, [query.isError, query.error, navigate, toast]);

  // G14.6 — RBAC gating on CTAs.
  const scope = useUserScope();
  const canEdit = canDo(scope, 'project', 'update');
  const canTransition = canDo(scope, 'project', 'update');

  /**
   * F2 (tier2-approval-requests §6) — активная заявка на этот проект.
   *
   * Производное поле ответа (B9), а не локальное состояние: после перезагрузки
   * страницы метка обязана остаться, потому что заявка никуда не делась.
   */
  const pendingApproval = (
    project as unknown as {
      pending_approval?: {
        id: string;
        task_type: string;
        addressee_user_id: string | null;
        expires_at: string | null;
      } | null;
    }
  )?.pending_approval;

  const [pendingTier2, setPendingTier2] = useState<ProjectStage | null>(null);
  // G5.5 — Health update drawer.
  const [healthDrawerOpen, setHealthDrawerOpen] = useState(false);
  // UX5 — Cancel project dialog.
  const [cancelOpen, setCancelOpen] = useState(false);
  /** F4 (project-plan-change) — диалог переноса срока. */
  const [planShiftOpen, setPlanShiftOpen] = useState(false);
  /** F8 — диалог возврата отменённого проекта в работу. */
  const [uncancelOpen, setUncancelOpen] = useState(false);
  /**
   * Превью отмены кормит ОБА диалога: отмене оно даёт четыре числа последствий,
   * возврату — сколько задач уже отменено и обратно не откроется. Один источник,
   * потому что оба ответа считаются одним классификатором на сервере.
   */
  const cancelPreviewQuery = useQuery({
    queryKey: ['project-cancel-preview', id],
    queryFn: () => fetchProjectCancelPreview(id),
    staleTime: 30_000,
  });
  // UX4 — overflow menu open state.
  // project-templates F2.4/F2.5 — диалоги «Сохранить как шаблон» / «Дублировать».
  const [templateDialog, setTemplateDialog] = useState<'save' | 'duplicate' | null>(null);

  /**
   * F15a — вход из ПКМ другого экрана: открыть нужный диалог и СРАЗУ вычистить
   * параметр (`replace`, без второй записи в истории). Иначе «назад» возвращал
   * бы в тот же диалог, а перезагрузка открывала бы его повторно.
   */
  const requestedAction = search.action;
  useEffect(() => {
    if (!requestedAction) return;
    if (requestedAction === 'status') setHealthDrawerOpen(true);
    if (requestedAction === 'cancel') setCancelOpen(true);
    if (requestedAction === 'plan_shift') setPlanShiftOpen(true);
    if (requestedAction === 'uncancel') setUncancelOpen(true);
    // CM-F3 (D4.11a) — «Дублировать» и «Сохранить как шаблон» из меню другого
    // экрана. Их диалоги живут здесь же, где и «Отмена», и якорь у них такой же:
    // без него меню доводило до карточки, а диалог пользователь открывал сам.
    if (requestedAction === 'duplicate') setTemplateDialog('duplicate');
    if (requestedAction === 'save_template') setTemplateDialog('save');
    // Стадия: конкретный переход выбирается в самой карточке — открываем меню
    // переходов, а не угадываем целевую стадию за пользователя.
    // F4 — якорь `?action=stage` больше не «раскрывает шапочное меню»: меню теперь
    // реестровое и открывается кликом по кебабу. Переходы стадии доступны кнопкой
    // и подменю «Стадия» того же реестра.
    // Чистим только свой параметр: остальные (вкладка, сортировка, фильтры)
    // принадлежат экрану и переживают вход из меню.
    void navigate({
      to: '/projects/$id',
      params: { id },
      search: ({ action: _drop, ...rest }) => rest as never,
      replace: true,
    });
  }, [requestedAction, id, navigate]);
  /**
   * F1 (tier2-approval-requests §5) — назван адресат, значит уходит ЗАЯВКА.
   *
   * Прежде отсюда уезжала выдуманная подпись, и человек считал действие сделанным.
   * Теперь при Tier 2 проект остаётся как есть, а решение принимает названный
   * человек; Tier 1 (стадия планирования) по-прежнему исполняется сразу.
   */
  const cancelMut = useMutation({
    mutationFn: (payload: { reason: string; addressee_user_id?: string }) =>
      submitProjectAction({
        projectId: id,
        taskType: 'project_cancel',
        reason: payload.reason,
        addresseeUserId: payload.addressee_user_id,
        direct: () => cancelProject(id, { reason: payload.reason }),
      }),
    onSuccess: (outcome) => {
      setCancelOpen(false);
      if (outcome.mode === 'requested') emitApprovalRequestedToast();
      qc.invalidateQueries({ queryKey: ['project', id] });
      // F2 — превью пересчитывается: после каскада состав задач другой, и
      // закэшированные числа обещали бы то, что уже произошло.
      qc.invalidateQueries({ queryKey: ['project-cancel-preview', id] });
    },
  });

  /**
   * F4 (project-plan-change §2.1) — перенос срока. Живёт здесь, рядом с отменой:
   * одно семейство lifecycle-решений — один экран, одна пара «диалог + мутация».
   */
  const planShiftMut = useMutation({
    mutationFn: (payload: PlanShiftInput) =>
      submitProjectAction({
        projectId: id,
        taskType: 'project_plan_shift',
        reason: payload.reason,
        addresseeUserId: payload.addressee_user_id,
        // Новые даты едут в теле заявки: исполнитель применит ИМЕННО их, а не то,
        // что окажется в форме на момент согласования.
        payload: {
          ...(payload.start_date !== undefined ? { start_date: payload.start_date } : {}),
          ...(payload.end_date !== undefined ? { end_date: payload.end_date } : {}),
        },
        direct: () => shiftProjectPlan(id, payload),
      }),
    onSuccess: (outcome) => {
      setPlanShiftOpen(false);
      if (outcome.mode === 'requested') emitApprovalRequestedToast();
      else emitToast({ variant: 'success', title: m.project_plan_shift_toast_ok() });
      qc.invalidateQueries({ queryKey: ['project', id] });
      // Превью сдвига считается от текущего плана — после переноса оно устарело.
      qc.invalidateQueries({ queryKey: ['project-plan-shift-preview', id] });
    },
  });
  /** F8 — разотмена. Инвалидации те же: исход меняется, портфельные счётчики тоже. */
  const uncancelMut = useMutation({
    mutationFn: (payload: { reason: string; addressee_user_id?: string }) =>
      submitProjectAction({
        projectId: id,
        taskType: 'project_uncancel',
        reason: payload.reason,
        addresseeUserId: payload.addressee_user_id,
        direct: () => uncancelProject(id, { reason: payload.reason }),
      }),
    onSuccess: (outcome) => {
      setUncancelOpen(false);
      if (outcome.mode === 'requested') emitApprovalRequestedToast();
      qc.invalidateQueries({ queryKey: ['project', id] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['projects-filter-counts'] });
    },
  });
  const transitionMut = useMutation({
    mutationFn: (payload: {
      target: ProjectStage;
      reason?: string;
      addressee_user_id?: string;
    }) =>
      submitProjectAction({
        projectId: id,
        // Тип заявки — по КОНЕЧНОЙ стадии: у завершения и закрытия разные правила
        // матрицы, и один общий тип стёр бы это различие.
        taskType: payload.target === 'closed' ? 'project_close' : 'project_complete',
        reason: payload.reason ?? 'manual',
        addresseeUserId: payload.addressee_user_id,
        direct: () =>
          transitionProjectStage(id, {
            target_stage: payload.target,
            reason: payload.reason ?? 'manual',
          }),
      }),
    onSuccess: () => {
      setPendingTier2(null);
      qc.invalidateQueries({ queryKey: ['project', id] });
    },
  });

  const handleStageClick = (target: ProjectStage): void => {
    // P13 — Tier 2 для completed/closed/cancelled (active+).
    if (target === 'completed' || target === 'closed') {
      setPendingTier2(target);
      return;
    }
    // detail UX-2: on_hold (pause) — also requires confirmation per
    // STAITER_UX «Write-действие ≥ Tier 2 → confirm». Pause = freeze
    // billing + halt task work, business-critical.
    if (target === 'on_hold') {
      setPendingTier2(target);
      return;
    }
    transitionMut.mutate({ target });
  };

  // PC-24: skeleton loading state — Hero block placeholder + tab content shimmer
  if (query.isLoading)
    return (
      <div style={{ display: 'grid', gap: 16, padding: 16 }}>
        <Skeleton width={240} height={28} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Skeleton height={80} />
          <Skeleton height={80} />
          <Skeleton height={80} />
        </div>
        <Skeleton height={320} />
      </div>
    );
  if (!project) return <div>Project not found</div>;

  /**
   * F12 (project-actions-parity §4.3) — один вызов на кнопку шапки и на подменю
   * «Стадия». Раньше шапка читала `PROJECT_STAGE_MATRIX` напрямую, а реестр —
   * через `allowedStages`; два вычисления одного списка разъехались бы на первой
   * же правке матрицы переходов, и кнопка предложила бы переход, которого в меню
   * нет (или наоборот).
   */
  const allowed = allowedStages(project.stage as ProjectStage);
  const setTab = (tab: TabKey) =>
    navigate({ to: '/projects/$id', params: { id }, search: { tab } as never });

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: m.nav_projects(), to: '/projects' }, { label: project.code }]}
          />
        }
        // FE-5 (Sprint 1 rework): split H1 — name primary, code subtitle.
        // Раньше «PRJ-2026-003 — Поставка мембран Цементный завод Семей» в H1 —
        // code занимает primary slot, name становится supporting. Теперь name = L1,
        // code в description в mono font как subtitle.
        /**
         * Гейт фазы C (UI-1/UI-3) — исход и стадия стоят В СТРОКЕ ЗАГОЛОВКА.
         *
         * Прежде бейдж исхода был третьим элементом подстрочника: до чтения текста
         * карточка отменённого проекта была неотличима от рабочей. Терминальность
         * обязана читаться раньше названия, а не после него.
         *
         * Стадия рядом — не украшение: `badge-tone.ts` сам объявляет, что «на какой
         * стадии проект отменили — ценный контекст», а её единственным носителем на
         * карточке была кнопка перехода, которая у отменённого больше не рендерится.
         */
        /**
         * Гейт фазы D — стадия в шапке БЕЗУСЛОВНА, а не только у отменённых.
         *
         * F9 забрал из кнопки существительное («→ В работе» → «Поставить на паузу»)
         * и тем снял с шапки единственного носителя состояния: у живого проекта
         * рядом с названием не осталось ни стадии, ни исхода. Оба ревьюера гейта
         * назвали это независимо. Формулировка UX: «Объект перестал показывать свой
         * статус на самом себе».
         *
         * Теперь кнопка отвечает «что сделать», бейдж — «где мы»; они стоят рядом и
         * не спорят, потому что говорят разными частями речи.
         *
         * Здоровье («В графике») по-прежнему показывается ТОЛЬКО при отклонении —
         * это правило Calm UI старше пака, с записанным обоснованием; гейт его не
         * пересматривает.
         */
        {...(project.status === 'cancelled'
          ? {
              titleAdornment: (
                <span className={styles.outcomeRow}>
                  {/* Второй проход гейта фазы C: ИСХОД первым и весомее стадии.
                      Тон `terminated` намеренно приглушён — в плотном списке это
                      верно, в шапке нет: глаз слева направо брал «Планирование», а
                      «Отменён» гас. Порядок и вес правятся здесь, а не в карте
                      тонов: список от этого меняться не должен. */}
                  {/* Гейт фазы C (UX-1): исход — утверждение, и у него обязан быть
                      путь к доказательству. Кто отменил, когда и с каким
                      обоснованием, лежит в журнале записью `cancel`; бейдж ведёт
                      туда, а не остаётся голым словом. */}
                  <Link
                    to="/projects/$id"
                    params={{ id: project.id }}
                    search={{ tab: 'audit' } as never}
                    className={styles.outcomeStrong}
                    aria-label={m.project_outcome_evidence_link()}
                    title={m.project_outcome_evidence_link()}
                  >
                    <StatusBadge
                      status={healthBadge({ health: 'on_track', status: project.status }).tone}
                      label={healthBadge({ health: 'on_track', status: project.status }).label}
                      size="md"
                    />
                  </Link>
                  {/* PPC-F22 (F18 гейт, обе половины, MAJOR): вторая пилюля «В работе»
                      рядом с «Отменён» читалась текущим статусом и спорила с исходом.
                      Конвенция «подпись = стадия на момент отмены» названа словами. */}
                  <span className={styles.stageAtCancel}>
                    {m.project_stage_at_cancel({
                      stage: stageBadge(project.stage as ProjectStage).label,
                    })}
                  </span>
                </span>
              ),
            }
          : {
              titleAdornment: (
                <span className={styles.outcomeRow}>
                  <StatusBadge
                    status={stageBadge(project.stage as ProjectStage).tone}
                    label={stageBadge(project.stage as ProjectStage).label}
                    size="sm"
                  />
                </span>
              ),
            })}
        title={project.name}
        description={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: 'var(--text-muted)',
                letterSpacing: '0.01em',
              }}
            >
              {project.code}
            </span>
            {/* Второй проход гейта фазы C: точка-разделитель печаталась безусловно,
                а клиента у проекта может не быть — за точкой оставалась пустота. */}
            {(project.customer_name ?? project.customer_id) && (
              <span style={{ color: 'var(--text-secondary)' }}>
                {' • '}
                {project.customer_name ?? project.customer_id}
                {project.sales_order_id && (
                  <>
                    {' • '}
                    <Link
                      to="/sales-orders/$id"
                      params={{ id: project.sales_order_id }}
                      style={{ color: 'var(--accent-strong)' }}
                    >
                      {project.sales_order_code ?? 'SO'}
                    </Link>
                  </>
                )}
                {project.expected_end_date && (
                  <>
                    {' • → '}
                    <span className={styles.dateInline}>
                      {formatDate(project.expected_end_date)}
                    </span>
                  </>
                )}
              </span>
            )}
            {/**
             * F7 (project-plan-change §6) — ИСХОД проекта в шапке карточки.
             *
             * До пака карточка не показывала его вовсе: у отменённого
             * `PROJ-2026-0012` на проде не было ни одного бейджа, зато акцентная
             * кнопка «→ В работе» стояла первым элементом. Экран звал продолжать
             * работу над проектом, который решили не делать.
             *
             * Помощник `healthBadge` был написан ещё прошлым паком и работает в
             * списке: он знает, что у отменённого здоровья нет и исход вытесняет
             * его. Карточка его просто не звала — правка не требует второй карты
             * тонов, только вызова.
             */}
            {/* project-visibility (F5) — бейдж «Закрытый» рядом с кодом/клиентом. */}
            <ProjectVisibilityBadge visibility={project.visibility} />
            {/**
             * F2 (tier2-approval-requests §6) — «ждёт согласования: отмена».
             *
             * Объект во время ожидания НЕ меняется, и без метки экран выглядит как
             * «нажал и ничего не произошло». Признак производный (B9), поэтому метка
             * переживает перезагрузку — в отличие от локального состояния.
             */}
            <PendingApprovalBadge pending={pendingApproval} />
            {/* FE-6 (Sprint 1 rework): hide «Состояние» pill для здоровых проектов.
                Calm Industrial — colour/badge только при отклонении. on_track —
                не показываем pill (свободное пространство, calm). at_risk/overdue/on_hold
                — badge visible с icon + label. */}
            {/* F7 — у отменённого проекта здоровья нет: «−12 дн» красным на том,
                что решили не делать, — не просрочка, а шум. Тот же принцип, что в
                списке, где `healthBadge` вытесняет здоровье исходом. */}
            {/* PPC-F20 — то же условие, что в реестре: у завершённого «−12 дн»
                красным сообщает отклонение хода работ, которого больше нет. */}
            {project.health !== 'on_track' && !isTerminalOutcome(project.status) && (
              <span
                title="Здоровье проекта — отклонение от плана"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '3px 10px',
                  borderRadius: 999,
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-primary)',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                <span
                  aria-hidden
                  style={{ color: HEALTH_DOT[project.health] ?? 'var(--text-muted)' }}
                >
                  {HEALTH_ICON[project.health] ?? '·'}
                </span>
                {PROJECT_HEALTH_LABEL[project.health]}
              </span>
            )}
          </span>
        }
        actions={
          <HeaderActions
            project={project}
            allowed={allowed}
            canEdit={canEdit}
            canTransition={canTransition}
            handleStageClick={handleStageClick}
            transitionPending={transitionMut.isPending}
            onEdit={() => navigate({ to: '/projects/$id/edit', params: { id } })}
            onUncancel={() => setUncancelOpen(true)}
            onSaveAsTemplate={() => setTemplateDialog('save')}
            onDuplicate={() => setTemplateDialog('duplicate')}
          />
        }
      />

      {templateDialog === 'save' && (
        <SaveAsTemplateDialog
          project={{ id, name: project.name }}
          onClose={() => setTemplateDialog(null)}
        />
      )}
      {templateDialog === 'duplicate' && (
        <DuplicateProjectDialog
          project={{ id, name: project.name }}
          onClose={() => setTemplateDialog(null)}
        />
      )}
      {/* Progress bar 4px under header.
          NC-38 (F17 пака tails-closeout): у `role="progressbar"` было значение без
          имени — «75%» узнавалось только из полосы ниже; стили жили inline мимо
          Stylelint-барьера. Имя и текст значения — через m.*, стили — в module.css;
          inline остаётся только ширина (данные, не оформление). */}
      {/* biome-ignore lint/a11y/useFocusableInteractive: progressbar — индикатор, а не виджет ввода; по WAI-ARIA фокус не принимает, имя и значение читаются с aria-label/aria-valuetext */}
      <div
        className={styles.headerProgress}
        role="progressbar"
        aria-label={m.project_header_progress_label()}
        aria-valuenow={Number(project.percent_complete)}
        aria-valuetext={`${formatNumber(project.percent_complete, { maximumFractionDigits: 1 })}%`}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={[
            styles.headerProgressFill,
            // PPC-F5 (решение владельца В-10): на терминальном исходе полоса
            // гасится. Акцентная заливка под шапкой — единственный элемент,
            // который у отменённого проекта продолжал сообщать «работа идёт».
            project.status === 'cancelled' || project.stage === 'closed'
              ? styles.headerProgressFillMuted
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{ width: `${Math.min(100, Number(project.percent_complete))}%` }}
        />
      </div>

      <PauseAwareTier2Dialog
        pendingTier2={pendingTier2}
        projectId={id}
        onConfirm={(payload) => {
          if (pendingTier2) {
            transitionMut.mutate({
              target: pendingTier2,
              reason: payload.reason,
              addressee_user_id: payload.addressee_user_id,
            });
          }
        }}
        onCancel={() => setPendingTier2(null)}
      />

      <HealthUpdateDialog
        open={healthDrawerOpen}
        projectId={id}
        currentHealth={project.health}
        onClose={() => setHealthDrawerOpen(false)}
        onSaved={() => {
          setHealthDrawerOpen(false);
          qc.invalidateQueries({ queryKey: ['project', id] });
        }}
      />

      {/* UX5 — Cancel project dialog (rendered if user opens overflow → Cancel). */}
      {(project.stage === 'planning' ||
        project.stage === 'active' ||
        project.stage === 'on_hold') && (
        <CancelProjectDialog
          open={cancelOpen}
          fromStage={project.stage as 'planning' | 'active' | 'on_hold'}
          // D5.4 (CM-F1) — идентичность объекта передаётся диалогу: он открывается
          // и по якорю `?action=cancel` со строки списка проектов, где карточки
          // на экране нет вовсе, и подтверждать приходилось вслепую.
          projectId={id}
          projectName={project.name}
          projectCode={project.code ?? null}
          stageLabel={PROJECT_STAGE_LABEL[project.stage] ?? project.stage}
          onConfirm={(payload) => cancelMut.mutate(payload)}
          onCancel={() => setCancelOpen(false)}
        />
      )}

      {/* F4 (project-plan-change) — перенос срока. Только у запущенного: на
          стадии планирования план правится обычной формой, и два пути к одному
          действию разъедутся. Ту же границу держит сервер. */}
      {project.stage !== 'planning' && (
        <PlanShiftDialog
          open={planShiftOpen}
          projectId={id}
          projectName={project.name}
          projectCode={project.code ?? null}
          startDate={project.expected_start_date ?? null}
          endDate={project.expected_end_date ?? null}
          onConfirm={(payload) => planShiftMut.mutate(payload)}
          onCancel={() => setPlanShiftOpen(false)}
        />
      )}

      {/* F8 — выход из отменённого состояния. Рендерится только у отменённого:
          разотменять нечего там, где отмены не было. */}
      {project.status === 'cancelled' && (
        <UncancelProjectDialog
          open={uncancelOpen}
          projectId={id}
          projectName={project.name}
          projectCode={project.code ?? null}
          // Число задач, которые обратно НЕ откроются, берётся из того же превью,
          // что кормит диалог отмены: возврат обязан быть доказательным ровно так же.
          cancelledTaskCount={cancelPreviewQuery.data?.already_cancelled_count ?? 0}
          onConfirm={(payload) => uncancelMut.mutate(payload)}
          onCancel={() => setUncancelOpen(false)}
        />
      )}

      <Tabs
        activeTab={activeTab}
        setTab={setTab}
        onLaunchTasks={() => navigate({ to: '/work-tasks/$projectId', params: { projectId: id } })}
      />
      {/* UX16 — keyboard shortcuts overlay (? key). */}
      <KeyboardShortcutsOverlay />

      <div style={{ marginTop: 16 }}>
        {activeTab === 'overview' && <OverviewTab project={project} />}
        {/* F2-full+F3 — задачи/гант/журнал работ живут на full-bleed task-module
            workspace (/work-tasks/$id, §3.4a — workspace не встраивается «иллюминатором»
            в content-page). Вкладка «Задачи» = launcher: redirect-эффект ниже уводит на
            workspace. Заменяет legacy TaskTreePanel/GanttPanel/TaskLogPanel. */}
        {(activeTab === 'tasks' || activeTab === 'gantt' || activeTab === 'task_log') && (
          <div
            style={{
              padding: 'var(--space-6, 32px)',
              textAlign: 'center',
              color: 'var(--text-muted)',
            }}
          >
            {m.ui_perehod_v_rabochee_prostranstvo_zadach()}
          </div>
        )}
        {activeTab === 'milestones' && <MilestonesPanel projectId={id} />}
        {activeTab === 'financial' && <FinancialPanel projectId={id} />}
        {activeTab === 'links' && <LinkagesPanel projectId={id} />}
        {activeTab === 'status_journal' && <StatusJournalTab project={project} />}
        {activeTab === 'audit' && (
          <AuditTab projectId={id} {...(search.audit ? { highlightId: search.audit } : {})} />
        )}
      </div>
    </div>
  );
}

/**
 * FE-69/70 (Sprint 9) — Wraps Tier2ApprovalDialog с pause-specific data fetch:
 *   - approver Select из BE-12 /projects/approvers (filtered by role + same entity)
 *   - impact preview из BE-12 /projects/:id/pause-impact (open tasks count, freed hours)
 *
 * Для complete/closed transitions показывает общий dialog без impact preview
 * (impact-builder для completion — отдельная задача FE-92 / BE-21).
 */
function PauseAwareTier2Dialog(props: {
  pendingTier2: ProjectStage | null;
  projectId: string;
  // F1 — вместо подписи адресат заявки: переход стадии Tier 2 отправляется на
  // решение названному человеку, а не применяется на месте.
  onConfirm: (payload: { addressee_user_id: string; reason: string }) => void;
  onCancel: () => void;
}): JSX.Element {
  const isPause = props.pendingTier2 === 'on_hold';
  const approversQuery = useQuery({
    queryKey: ['project-approvers', isPause ? 'project.pause' : 'project.complete'],
    queryFn: () =>
      fetchProjectApprovers({ action: isPause ? 'project.pause' : 'project.complete' }),
    enabled: !!props.pendingTier2,
    staleTime: 60_000,
  });
  // F12 / PLM-6 (B20) — что мешает закрыть: показывается до подписи, не после 422.
  const isClose = props.pendingTier2 === 'closed';
  const closeoutQuery = useQuery({
    queryKey: ['project-closeout', props.projectId],
    queryFn: () => fetchProjectCloseout(props.projectId),
    enabled: isClose,
    staleTime: 10_000,
  });
  const impactQuery = useQuery({
    queryKey: ['project-pause-impact', props.projectId],
    queryFn: () => fetchProjectPauseImpact(props.projectId),
    enabled: !!props.pendingTier2 && isPause,
    staleTime: 30_000,
  });
  const approverOptions = (approversQuery.data?.items ?? []).map((a) => ({
    id: a.id,
    label: a.full_name ?? a.email,
    role: a.role,
  }));
  const impact = impactQuery.data;
  return (
    <Tier2ApprovalDialog
      open={!!props.pendingTier2}
      title={m.project_stage_transition_title({
        stage: props.pendingTier2
          ? (PROJECT_STAGE_LABEL[props.pendingTier2] ?? props.pendingTier2)
          : '',
      })}
      description={m.tier2_dialog_description()}
      approverOptions={approverOptions}
      impactPreview={
        isPause && impact ? (
          /* FE-119 (Sprint 12 polish from S9B3 review F-S9B3-2):
             Hero Metric grid 3-col (label сверху, large tabular-nums число снизу)
             вместо inline middle-dot row. */
          <div style={{ fontSize: 13, display: 'grid', gap: 8 }}>
            {/* FE-118 (Sprint 12 polish from S9B3-1): AlertTriangle icon + tonal bg вместо только border */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontWeight: 500,
                color: 'var(--color-warning, #d97706)',
              }}
            >
              <AlertTriangle size={16} aria-hidden="true" />
              {m.ui_vliyanie_pauzy_na_proekt()}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                gap: 8,
              }}
            >
              <ImpactStat label="Открытых задач" value={String(impact.open_tasks_count)} />
              <ImpactStat
                label="Часы освобождаются"
                value={impact.freed_capacity_hours}
                suffix="ч"
              />
              <ImpactStat
                label="Draft AR-инвойсов"
                value={String(impact.draft_ar_invoices_count)}
              />
            </div>
          </div>
        ) : isClose && closeoutQuery.data ? (
          <CloseoutBlockers preview={closeoutQuery.data} onNavigate={props.onCancel} />
        ) : null
      }
      onConfirm={props.onConfirm}
      onCancel={props.onCancel}
    />
  );
}

/** FE-119 — mini Hero Metric для pause impact (label small + tabular-nums число). */
function ImpactStat(props: { label: string; value: string; suffix?: string }): JSX.Element {
  return (
    <div
      style={{
        padding: '6px 8px',
        background: 'rgba(0,0,0,0.18)',
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <span
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-muted)',
        }}
      >
        {props.label}
      </span>
      <span
        style={{
          fontSize: 16,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--text-primary)',
        }}
      >
        {props.value}
        {props.suffix ? ` ${props.suffix}` : null}
      </span>
    </div>
  );
}

function Tabs(props: {
  activeTab: TabKey;
  setTab: (t: TabKey) => void;
  // F-PERF — «Задачи» = launcher: прямая навигация в /work-tasks без промежуточного
  // ?tab=tasks → redirect-эффект round-trip (убирает double-mount + 429-burst).
  onLaunchTasks: () => void;
}): JSX.Element {
  // FE-93 (QA-2026-05-27): 9 tabs нарушает Progressive Disclosure §3.3 (Level 0→1→2 max).
  // Group 3 audit tabs (task_log/status_journal/audit) в «Журналы ▾» dropdown.
  type TabDef = { key: TabKey; label: string; tooltip?: string };
  // F2-full — «Гант» поглощён встроенным workspace под «Задачи» (Список/Канбан/Гант/
  // Календарь). «Журнал работ» (task_log) убран из таб-бара — worklog через peek задачи.
  const primaryTabs: TabDef[] = [
    { key: 'overview', label: m.project_tab_overview() },
    { key: 'tasks', label: m.project_tab_tasks() },
    { key: 'milestones', label: m.project_tab_milestones() },
    { key: 'financial', label: m.project_tab_financial() },
    { key: 'links', label: m.project_tab_links() },
  ];
  const journalTabs: TabDef[] = [
    {
      key: 'status_journal',
      label: m.project_tab_status_journal(),
      tooltip: m.project_tab_status_journal_tooltip(),
    },
    { key: 'audit', label: m.project_tab_audit(), tooltip: m.project_tab_audit_tooltip() },
  ];
  const activeJournalTab = journalTabs.find((t) => t.key === props.activeTab);
  const [journalsOpen, setJournalsOpen] = useState(false);
  return (
    <nav
      style={{
        display: 'flex',
        gap: 4,
        borderBottom: '1px solid var(--border)',
        alignItems: 'center',
      }}
      role="tablist"
    >
      {primaryTabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={props.activeTab === t.key}
          onClick={() => (t.key === 'tasks' ? props.onLaunchTasks() : props.setTab(t.key))}
          style={{
            padding: '8px 14px',
            background: props.activeTab === t.key ? 'var(--bg-elevated)' : 'transparent',
            color: props.activeTab === t.key ? 'var(--text-heading)' : 'var(--text-secondary)',
            fontWeight: props.activeTab === t.key ? 600 : 400,
            border: 'none',
            borderBottom:
              props.activeTab === t.key ? '2px solid var(--accent)' : '2px solid transparent',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          {t.label}
        </button>
      ))}
      {/* Journals dropdown — groups 3 audit tabs per FE-93 */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          type="button"
          role="tab"
          aria-selected={!!activeJournalTab}
          aria-expanded={journalsOpen}
          onClick={() => setJournalsOpen(!journalsOpen)}
          style={{
            padding: '8px 14px',
            background: activeJournalTab ? 'var(--bg-elevated)' : 'transparent',
            color: activeJournalTab ? 'var(--text-heading)' : 'var(--text-secondary)',
            fontWeight: activeJournalTab ? 600 : 400,
            border: 'none',
            borderBottom: activeJournalTab ? '2px solid var(--accent)' : '2px solid transparent',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {activeJournalTab ? activeJournalTab.label : m.project_tabs_journals_group()}
            {/* PC-18: Caret ALWAYS visible — даже когда sub-tab active. Dropdown — это
                continuous affordance, не toggle. Pattern documented в STAITER_UX:
                «dropdown-tabs показывают caret независимо от active state, чтобы PM
                видел что внутри ещё подзаголовки». */}
            <ChevronDown size={12} aria-hidden="true" />
          </span>
        </button>
        {journalsOpen && (
          <div
            role="menu"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              minWidth: 200,
              marginTop: 4,
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              zIndex: 10,
            }}
          >
            {journalTabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="menuitem"
                title={t.tooltip}
                onClick={() => {
                  props.setTab(t.key);
                  setJournalsOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  background: props.activeTab === t.key ? 'var(--bg-base)' : 'transparent',
                  color: 'var(--text-primary)',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}

/** UX4 — Header actions: 1 primary CTA по stage + overflow menu для secondary. */
function HeaderActions(props: {
  project: Awaited<ReturnType<typeof fetchProject>>;
  allowed: readonly ProjectStage[];
  canEdit: boolean;
  canTransition: boolean;
  handleStageClick: (target: ProjectStage) => void;
  transitionPending: boolean;
  onEdit: () => void;
  /** Гейт фазы C (UX-2) — выход из терминального исхода, видимым действием. */
  onUncancel: () => void;
  onSaveAsTemplate: () => void;
  onDuplicate: () => void;
}): JSX.Element {
  const {
    project,
    allowed,
    canEdit,
    canTransition,
    handleStageClick,
    transitionPending,
    onEdit,
    onUncancel,
    onSaveAsTemplate,
    onDuplicate,
  } = props;
  /**
   * F12 (project-actions-parity §4.3) — кнопка стадии и подменю «Стадия» кормятся
   * ОДНИМ вызовом `allowedStages`. `allowed` приходит сверху из того же источника;
   * до правки шапка считала список сама (`PROJECT_STAGE_MATRIX[...]` в route-файле),
   * а реестр — своим `allowedStages`, и два вычисления одного списка разъехались бы
   * на первой правке матрицы переходов.
   */
  /**
   * PPC-F12 (решение владельца В-10): главным действием шапки становится
   * ОЖИДАЕМЫЙ шаг жизненного цикла, а не первый элемент массива допустимых.
   * У активного проекта матрица перечисляет `['on_hold', 'completed']`, и
   * primary доставался паузе — исключению, а не следующему шагу.
   */
  const primary = forwardStage(project.stage as ProjectStage) ?? allowed[0];
  /**
   * PPC-F15 (решение владельца 30.08.2026: «Да») — при открытых просрочках
   * терминальный ожидаемый шаг теряет АКЦЕНТ, но не доступность.
   *
   * Находка UX-половины гейта дословно: «„Завершить“ акцентной кнопкой у проекта
   * с 0 завершённых и 3 просроченными задачами делает громче всего необратимый
   * переход, а не нужный шаг». Кнопка остаётся на месте и работает — руководитель
   * вправе закрыть проект с хвостами; экран лишь перестаёт этого ТРЕБОВАТЬ, и
   * самым громким на нём остаётся ссылка на сами просроченные задачи.
   *
   * Гасится только необратимое: `forwardStage` из `planning`/`on_hold` ведёт в
   * `active`, и приглушать «В работу» из-за просрочек значило бы мешать как раз
   * тому действию, которым их разгребают.
   */
  const overdueOpen = project.task_summary?.overdue ?? 0;
  const terminalStepMuted = primary !== undefined && isTerminalStage(primary) && overdueOpen > 0;
  /**
   * F4 — меню собирает РЕЕСТР, а не разметка в этом файле. Снесены: 6-пунктовое
   * рукописное меню, три объекта инлайновых стилей (`menuItemStyle`,
   * `menuSectionLabelStyle`, `menuSeparatorStyle`), сырая тень `rgba(0,0,0,0.2)`
   * при живом `--ctxmenu-shadow`, две грамматики стрелки и пункт-заглушка
   * «Шаблон уведомлений →», который вёл в глобальную админку и действием над
   * проектом не является (SOLUTION §4.3).
   *
   * Гейт отмены (`showCancel` по стадии) тоже снесён: правило живёт в реестре,
   * и двух правил у одного действия быть не должно.
   */
  const buildMenu = useProjectActions({ surface: 'card', canMoveEntity: false });
  const menuArgs = buildMenu({
    id: project.id,
    name: project.name,
    code: project.code ?? null,
    stage: project.stage as ProjectStage,
    status: project.status,
    visibility: (project.visibility ?? 'open') as 'open' | 'private',
    // Легаси-схема `Project` этого маршрута отдаёт `archived_at` нетипизированным;
    // сюда нужен ISO-string или null, поэтому сужаем явно.
    archived_at: typeof project.archived_at === 'string' ? project.archived_at : null,
    // F2 — та же производная, что кормит метку: меню и метка обязаны говорить
    // одно, иначе пункт останется активным рядом с надписью «ждёт согласования».
    pending_approval:
      (project as unknown as { pending_approval?: { id: string; task_type: string } | null })
        .pending_approval ?? null,
  });
  return (
    <div style={{ position: 'relative', display: 'inline-flex', gap: 8 }}>
      {/**
       * F7 (project-plan-change §6) — на терминальном исходе кнопка перехода стадии
       * не предлагается ВОВСЕ.
       *
       * У отменённого проекта акцентная «→ В работе» стояла первым элементом шапки:
       * экран приглашал продолжить работу над тем, что решили не делать. Из действий
       * остаются «Разотменить» и «Вернуть из архива» — они живут в меню реестра и
       * там же гейтятся.
       */}
      {canTransition && primary && project.status !== 'cancelled' && (
        // UI-2: «На паузе» (regressive transition) → secondary; forward-progress
        // transitions (active/completed) → primary accent gold.
        <Button
          size="sm"
          // Возврат из паузы и переоткрытие завершённого — допустимые, но не
          // ожидаемые шаги: акцент им не положен (В-10).
          variant={primary === 'on_hold' || terminalStepMuted ? 'secondary' : 'primary'}
          onClick={() => handleStageClick(primary)}
          disabled={transitionPending}
          {...(terminalStepMuted
            ? { title: m.project_stage_terminal_muted_title({ count: overdueOpen }) }
            : {})}
        >
          {/* F9 — глагол из реестра вместо `→ {название стадии}`. Стрелка со
              существительным читалась и как «мы здесь», и как «идём сюда»;
              стрелку убрали вместе с существительным — при глаголе она лишняя. */}
          {stageActionLabel(primary, project.stage as ProjectStage)}
        </Button>
      )}
      {/**
       * Гейт фазы C (UX-2) — на терминальном исходе видимым акцентом становится
       * ВЫХОД из состояния, а не правка.
       *
       * Кнопку перехода стадии у отменённого мы сняли (она звала продолжать
       * мёртвое), но акцентным на экране осталось «Редактировать», а единственный
       * выход — «Разотменить» — был спрятан за кебабом: пользователь обязан был
       * его угадать. Действие ведёт в тот же диалог, что и пункт реестра, —
       * якорем, а не второй реализацией.
       */}
      {project.status === 'cancelled' && (
        <DisabledWithReason ability="project:delete" can={canTransition}>
          {/* Второй проход UX: «не прятать» ≠ «сделать самым тёмным пятном».
              Частое намерение на терминальной карточке — чтение истории, а не
              восстановление; вес secondary, место — видимое. */}
          <Button size="sm" variant="secondary" onClick={onUncancel}>
            {m.project_uncancel_cta()}
          </Button>
        </DisabledWithReason>
      )}
      <DisabledWithReason ability="project:update" can={canEdit}>
        <Button size="sm" variant="secondary" onClick={onEdit}>
          {m.project_cta_edit()}
        </Button>
      </DisabledWithReason>
      {/* F4 — видимый носитель меню. Открывается ОТ КНОПКИ (F3): при активации
          с клавиатуры `clientX/clientY` события равны нулю, и меню, открытое
          `openContextMenuFromEvent`, уезжало бы в угол экрана. */}
      <Button
        size="sm"
        variant="ghost"
        aria-label={m.project_header_more_actions()}
        aria-haspopup="menu"
        disabled={menuArgs === null}
        onClick={(e) => openContextMenuFromButton(e.currentTarget, menuArgs)}
      >
        <MoreHorizontal size={14} />
      </Button>
    </div>
  );
}

function OverviewTab(props: { project: Awaited<ReturnType<typeof fetchProject>> }): JSX.Element {
  const p = props.project;
  /**
   * PPC-F5 / PPC-F8 (решение владельца В-10 от 28.08.2026) — терминальная карточка
   * читается терминальной.
   *
   * Отменённый и завершённо-закрытый проект остаются с полными числами (историю не
   * прячем), но перестают выглядеть живыми: метрики приглушаются, зелёная отметка
   * завершённых задач гаснет. До этой правки «ПРОГРЕСС 42,5 %» на отменённом был
   * набран тем же чернильным тоном, что у активного, и продолжал сообщать ход
   * работ, которых нет.
   */
  /**
   * PPC-F20: терминальность читается по НАБОРУ исходов, а не по одному значению
   * плюс одной стадии. Прежнее условие пропускало самый частый терминальный
   * случай — проект на стадии `completed` (закрыт, но ещё не «closed»): обзор
   * такого проекта продолжал светиться как рабочий.
   */
  const isTerminal = isTerminalOutcome(p.status) || p.stage === 'closed';
  const completedTone = p.task_summary && p.task_summary.completed > 0 && !isTerminal ? 'ok' : null;
  /**
   * PPC-F6 (F17 пака tails-closeout, BE — B26): у отменённого проекта первый вопрос
   * читателя — когда, кто и почему. Ответ жил за переходом в журнал; поля
   * `cancelled_at / cancelled_by / cancel_reason` пришли с B26, имя автора —
   * `cancelled_by_name` из ручки карточки (пул пользователей компании не знает
   * глобального админа и ушедших). Имени нет — часть «кем» не выводим, UUID не печатаем.
   */
  const isCancelled = p.status === 'cancelled';
  const cancellerName = p.cancelled_by_name ?? undefined;
  /**
   * PPC-F11: «сметы нет» и «смета равна нулю» — разные факты, и на экране они
   * обязаны выглядеть по-разному. Ноль здесь не бывает осмысленным: проект с
   * нулевой плановой себестоимостью — это проект, у которого её не объявляли.
   */
  const costAbsent = !p.estimated_cost || Number(p.estimated_cost.amount) === 0;
  // detail UX-1 (CRITICAL): 3 Hero Metrics Level 0.
  const endDate = p.expected_end_date ? new Date(p.expected_end_date) : null;
  const today = new Date();
  const daysToDeadline = endDate
    ? Math.round((endDate.getTime() - today.getTime()) / 86_400_000)
    : null;
  return (
    // Раунд 1 гейта PPC (обе половины): «приглушение обошло полосу маржи — на
    // отменённом проекте самое яркое число экрана это „0,00 ₸“, а hero-прогресс
    // уведён в muted». Верно: правило В-10 висело на плитках, а не на блоке.
    // Теперь терминальность — свойство ОБЗОРА целиком.
    <div
      className={isTerminal ? styles.overviewTerminal : undefined}
      style={{ display: 'grid', gap: 16 }}
    >
      {/* PPC-F6 (F18 гейт, UX MAJOR): ответ на первый вопрос по отменённому проекту
          стоял четвёртым блоком, под метриками. Теперь — первым. */}
      {isCancelled && (
        <p className={styles.cancelFactsRow}>
          <span className={styles.cancelFactsLabel}>{m.project_status_cancelled()}</span>
          {p.cancelled_at || cancellerName || p.cancel_reason ? (
            <span className={styles.cancelFacts}>
              {p.cancelled_at ? (
                <span className={styles.cancelDate}>{formatDate(String(p.cancelled_at))}</span>
              ) : null}
              {[
                cancellerName ?? null,
                p.cancel_reason
                  ? m.project_overview_cancel_reason({ reason: p.cancel_reason })
                  : null,
              ]
                .filter((x): x is string => x !== null)
                .map((part, i) => (
                  <span key={part}>
                    {i > 0 || p.cancelled_at ? ' · ' : ''}
                    {part}
                  </span>
                ))}
            </span>
          ) : (
            <Link
              to="/projects/$id"
              params={{ id: p.id }}
              search={{ tab: 'audit' } as never}
              className={styles.planShift}
            >
              {m.project_overview_cancel_no_details()}
            </Link>
          )}
        </p>
      )}
      {/* Hero Metrics — 3 column grid */}
      {/* Гейт фазы C (UI-2): у отменённого «ДО СРОКА —» занимало треть героя и не
          несло ничего. Считать дни до срока проекта, который решили не делать,
          нечего — плитка убирается, сетка схлопывается до двух колонок. */}
      <div
        style={{
          display: 'grid',
          // Второй проход гейта: сетка ВСЕГДА трёхколоночная — иначе геометрия
          // карточки прыгает между проектами, а плитка растягивается под
          // 48-пиксельное число. У отменённого первая плитка занимает две колонки.
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 12,
        }}
      >
        <HeroMetric
          {...(p.status === 'cancelled' ? { className: styles.heroSpanTwo } : {})}
          label={m.project_col_progress()}
          icon={BarChart3}
          // Путь от числа к правилу: метод расчёта живёт на вкладке «Финансы»
          // (там reason-гейт и аудит), и без ссылки Hero-метрика была тупиком —
          // пользователь видел процент, но не мог дойти до того, чем он посчитан.
          {...(p.status === 'cancelled'
            ? {}
            : {
                // Гейт фазы C (UI-4): у отменённого это та же «продолжайте работу»,
                // что и снятая кнопка стадии, только единственным цветом экрана.
                action: {
                  label: m.tm_progress_hero_change_method(),
                  to: `/projects/${p.id}?tab=financial`,
                },
              })}
          value={`${formatNumber(p.percent_complete, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}%`}
          // F4-rework (design-review UX-2) — подпись описывает СВОЙ метод.
          // Прежняя дробь «0 / 3» принадлежала методу «доля завершённых»: под
          // средним прогрессом она читалась как противоречие (ноль завершено,
          // а показано 25%) и дублировала строку «Задачи» ниже.
          subtle={(() => {
            const method = p.percent_complete_method;
            if (method === 'manual') return m.tm_progress_hero_subtitle_manual();
            const s = p.task_summary;
            if (!s) return PERCENT_COMPLETE_METHOD_I18N[method].label;
            const leaves = s.leaf_count ?? s.total;
            const base =
              method === 'task_completion'
                ? m.tm_progress_hero_subtitle_completion({ done: s.completed, total: leaves })
                : method === 'task_weight'
                  ? m.tm_progress_hero_subtitle_weighted({ leaves })
                  : m.tm_progress_hero_subtitle_avg({ leaves });
            // Ниже на той же карточке строка «Задачи» показывает ДРУГОЕ число —
            // все задачи проекта. Два знаменателя рядом без объяснения читаются
            // как ошибка счёта; называем разницу прямо здесь, а не в подсказке
            // на другой вкладке.
            const excluded = Math.max(0, s.total - leaves);
            // Rework дизайн-гейта D4, раунд 3 (UX-ревьюер): ход к слагаемым был
            // ОДНОСТОРОННИМ — исключение вело к своим записям, а сам знаменатель
            // главной цифры никуда не вёл. Обе части подписи — переходы: база в
            // задачи проекта, исключение — в свой срез.
            // Rework раунда 4 (оба ревьюера): подчёркнутыми были ОБЕ части, и
            // граница между «в задачи проекта» и «в срез» пропадала — одна
            // сплошная подчёркнутая строка. Подчёркивание оставлено хвосту,
            // база остаётся обычным текстом, но кликабельна (курсор + hover).
            const baseLink = (
              <Link
                to="/work-tasks/$projectId"
                params={{ projectId: p.id }}
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {base}
              </Link>
            );
            if (excluded === 0) return baseLink;
            // Z-F6 (D4.10) — число вне расчёта ведёт к своим записям. Раньше это был
            // тупик: пользователь узнавал, что N задач не в счёте, и не мог узнать
            // КАКИЕ — а знаменатель процента без состава не проверяется.
            return (
              <>
                {baseLink} ·{' '}
                <Link
                  to="/work-tasks/$projectId"
                  params={{ projectId: p.id }}
                  search={{ progress: 'excluded' }}
                  aria-label={m.tm_progress_hero_excluded_link_aria({ count: excluded })}
                  // Аффорданс тот же, что у соседней «N просрочено →» на этой же
                  // карточке: точечное подчёркивание + стрелка. Без него ссылка
                  // была неотличима от подписи — тупик оставался визуальным,
                  // хотя технически уже был пройден (находка дизайн-гейта D4).
                  style={{
                    color: 'inherit',
                    textDecoration: 'underline dotted',
                    textUnderlineOffset: '2px',
                  }}
                >
                  {excluded === 1
                    ? m.tm_progress_hero_excluded_note_one()
                    : m.tm_progress_hero_excluded_note({ count: excluded })}{' '}
                  →
                </Link>
              </>
            );
          })()}
          // Правило, по которому посчитано число, стоит на самой карточке —
          // читательское намерение закрывается на месте. Ссылка ведёт в
          // редактор и называется тем, что там делают: раньше она обещала
          // «как считается», а приземляла в форму с «Отмена» и «Сохранить».
          // Rework дизайн-гейта D4: правило показываем ТОЛЬКО когда подпись выше
          // его не содержит. При наличии сводки задач подпись уже называет и метод,
          // и знаменатель («Среднее по 2 задачам нижнего уровня»), и вторая строка
          // повторяла ту же мысль другими словами прямо под числом.
          {...(p.task_summary
            ? {}
            : { methodHint: PERCENT_COMPLETE_METHOD_I18N[p.percent_complete_method].hint })}
        />
        {/* PPC-F11 — отсутствие сметы перестало показываться посчитанным нулём.
            «Плановая себестоимость 0,00 ₸» у проекта без сметы — это нарушение
            правила «частичное не выглядит успехом»: ноль читается как результат
            расчёта, хотя считать было нечего. Приём тот же, что у соседней плитки
            прогресса, у которой есть «Изменить метод →»: пустое состояние со своим
            действием, а не число-обманка. */}
        <HeroMetric
          label={m.project_form_estimated_cost()}
          icon={Coins}
          value={costAbsent ? '—' : formatMoney(p.estimated_cost)}
          subtle={costAbsent ? m.project_hero_cost_absent() : null}
          {...(costAbsent && !isTerminal
            ? {
                action: {
                  label: m.project_hero_cost_add(),
                  to: `/projects/${p.id}?tab=financial`,
                },
              }
            : {})}
        />
        {/* F-DETAIL-POLISH (из F2 + B10 review, оба дизайнера): overdue «Сроки» должен
            читаться как danger-СОСТОЯНИЕ, а не нейтральный текст. Число окрашивается
            (color без borderOnly) → grammar состояния = цвет + иконка + знак-минус +
            border-left (WCAG 1.4.1 не only-color). Заменяет FE-162 single-signal
            (border-only был слишком тих для критичной просрочки — verbatim verdict). */}
        {/* Гейт фазы C (UI-2): у отменённого плитка не рендерится ВОВСЕ. Считать
            дни до срока проекта, который решили не делать, нечего, а «—» на треть
            героя — мёртвое место. Сетка выше схлопывается до двух колонок. */}
        {p.status !== 'cancelled' && (
          <HeroMetric
            /* Гейт фазы B (UI-3 / UX-5): подпись «СРОКИ» стояла и здесь, и над
             строкой плана, а числа считали разное — «сколько осталось» против
             «насколько уехал план». Плитка называет свой счёт. */
            label={m.project_hero_until_deadline()}
            icon={Clock}
            value={
              daysToDeadline === null
                ? '—'
                : daysToDeadline < 0
                  ? `−${-daysToDeadline} дн`
                  : daysToDeadline === 0
                    ? 'сегодня'
                    : `${daysToDeadline} дн`
            }
            subtle={
              !p.expected_end_date ? (
                // Раунд 1 гейта PPC (обе половины): «плитка „ДО СРОКА“ пустая: „—“
                // без подписи … соседняя плитка себя объясняет („сметы нет“) —
                // грамматика пустоты в одном ряду разная». Пустота называется.
                <span>{m.project_hero_deadline_absent()}</span>
              ) : (
                <span className={styles.dateInline}>
                  {formatDate(p.expected_end_date)}
                  {/* PPC-F4 — плитка перестала МОЛЧА наследовать сдвинутую дату.
                      Число «29 дн» считается от текущего плана, а пометка о переносе
                      жила строкой ниже, в поле «СРОКИ»: человек, читающий герой,
                      не знал, что срок уже двигали, и «29 дн» выглядело исходным
                      обещанием. Величина и путь к ней остаются в поле «СРОКИ» —
                      здесь только признак, что обещание не первое. */}
                  {typeof p.plan_shift_days === 'number' && p.plan_shift_days !== 0 && (
                    <span className={styles.deadlineShifted}>
                      {' · '}
                      {m.project_hero_deadline_shifted()}
                    </span>
                  )}
                </span>
              )
            }
            color={
              daysToDeadline === null
                ? undefined
                : daysToDeadline < 0
                  ? 'danger'
                  : daysToDeadline < 7
                    ? 'warn'
                    : undefined
            }
          />
        )}
      </div>

      {/* Спринт D (F-DETAIL-POLISH): маржа = first-class метрика (из cost-engine, B8-defer
          live), не строка в примечаниях-prose. Цвет по знаку, % + provenance-chip. */}
      {p.gross_margin && (
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            padding: '12px 16px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: 'var(--bg-elevated)',
          }}
        >
          <span
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'var(--text-muted)',
            }}
          >
            {m.project_financial_gross_margin_fact()}
          </span>
          <span
            style={{
              fontSize: 20,
              fontWeight: 600,
              fontFamily: 'var(--font-mono)',
              fontVariantNumeric: 'tabular-nums',
              // Ноль не «хорошо» и не «плохо» — цвет только при отклонении
              // (§1.3). `>= 0` красил нулевую маржу в зелёное, а стоящий рядом
              // процент был уже нейтральным: два числа одной строки жили по
              // разным правилам.
              color:
                Number(p.gross_margin.amount) === 0
                  ? 'var(--text-primary)'
                  : Number(p.gross_margin.amount) > 0
                    ? 'var(--ok)'
                    : 'var(--danger)',
            }}
          >
            {formatMoney(p.gross_margin)}
          </span>
          <span
            style={{
              fontSize: 13,
              fontFamily: 'var(--font-mono)',
              // UI-review: при отрицательной марже % синхронно с суммой → danger (status
              // не только на сумме); positive — нейтральный (calm, не дублируем зелёным).
              color: Number(p.gross_margin.amount) >= 0 ? 'var(--text-secondary)' : 'var(--danger)',
            }}
          >
            {`· ${formatNumber(p.per_gross_margin, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}%`}
          </span>
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 10,
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-muted)',
              border: '1px solid var(--border)',
              borderRadius: 3,
              padding: '2px 6px',
            }}
            title="Источник: STAITER cost-engine"
          >
            STAITER
          </span>
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        <Field label={m.project_col_customer()}>
          {p.customer_id ? (
            <Link to="/counterparties/$id" params={{ id: p.customer_id }}>
              {p.customer_name ?? p.customer_id}
            </Link>
          ) : (
            <span>—</span>
          )}
        </Field>
        {p.sales_order_id && (
          <Field label={m.project_form_so()}>
            <Link to="/sales-orders/$id" params={{ id: p.sales_order_id }}>
              {p.sales_order_code ?? p.sales_order_id}
            </Link>
          </Field>
        )}
        {/* Второй проход гейта фазы C: «— → —» — стрелка между двумя прочерками.
            Действующий паттерн `skip-null-optional-rows`: пустая необязательная
            строка не рендерится вовсе. */}
        {(p.expected_start_date || p.expected_end_date) && (
          <Field label={m.project_form_dates()}>
            {/* Гейт фазы B (UI-5): те же даты в диалоге переноса моноширинные, а
              здесь шли пропорциональным Inter — одно и то же читалось двумя
              наборами цифр. */}
            {/* Второй проход гейта: карточка печатала сырой ISO, диалог переноса —
              `formatDate`. Одна и та же дата в двух форматах на связанной паре
              поверхностей: гарнитуру свели, формат — нет. */}
            <span className={styles.planDates}>
              {formatDate(p.expected_start_date)} → {formatDate(p.expected_end_date)}
            </span>
            {/* F6 (project-plan-change §4.3) — сдвиг рядом с планом.
              Без него базовая линия существует только в базе: человек видит
              «2026-07-23 → 2026-08-04» и не знает, что обещано было на две недели
              раньше. Число берётся из ответа сервера (`plan_shift_days`, B9), а не
              считается здесь: одно правило на всех потребителей.
              Нулевой сдвиг и отсутствие базовой линии не показываются вовсе —
              «сдвинут на 0 дней» не сообщение, а шум. Тон нейтральный: сдвиг —
              факт, а не отклонение (SSOT §1.3). */}
            {/* Гейт фазы B (UX-2): сдвиг был мёртвым текстом. Evidence-first требует
              пути к доказательству — кто перенёс, когда и с каким обоснованием.
              Ведёт в журнал проекта, где лежит запись `plan_shift` с прежними и
              новыми датами, сдвигом и перечнем оснований. */}
            {typeof p.plan_shift_days === 'number' && p.plan_shift_days !== 0 && (
              <Link
                to="/projects/$id"
                params={{ id: p.id }}
                search={{ tab: 'audit' } as never}
                className={styles.planShift}
                aria-label={m.project_card_plan_shift_link_aria()}
              >
                {p.plan_shift_days > 0
                  ? m.project_card_plan_shift({ days: formatNumber(p.plan_shift_days) })
                  : m.project_card_plan_shift_back({ days: formatNumber(-p.plan_shift_days) })}
              </Link>
            )}
          </Field>
        )}
        {p.task_summary && (
          <Field label={m.project_tab_tasks()}>
            {/* Тон отметки: `ok` — только когда завершённые есть И проект жив.
                На терминальном исходе счёт остаётся (историю не прячем), но
                перестаёт праздноваться. */}
            {/* detail UX-5: format clearer — «5 задач · 2 завершено · 0 просрочено» */}
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {pluralTasks(p.task_summary.total)}
              {' · '}
              {/* PPC-F8 (свод tails-sweep-2, заход 5) — семантический цвет только
                  при отклонении (§1.3), и только на достижении.

                  Было: зелёная галочка на «0 завершено», в том числе у отменённого
                  проекта, — цвет успеха на отсутствии успеха. Плюс HEX-фолбэк
                  `var(--ok, #16a34a)` в инлайн-стиле: фолбэк прячет фантомный токен
                  и красит мимо палитры темы. Теперь зелёное — только когда есть
                  что зачесть, и только на живом проекте; иначе нейтрально и без
                  галочки. */}
              <span className={completedTone === null ? undefined : styles.tasksDone}>
                {completedTone === 'ok' ? '✓ ' : ''}
                {m.project_tasks_completed_n({ count: p.task_summary.completed })}
              </span>
              {p.task_summary.overdue > 0 && (
                <>
                  {' · '}
                  {/* F-DETAIL-POLISH: «N просрочено» = flow-continuity drilldown в
                      task-workspace (designer-consensus — путь от сигнала к действию). */}
                  <Link
                    to="/work-tasks/$projectId"
                    params={{ projectId: p.id }}
                    // UI-review: underline-affordance в rest-состоянии — drilldown
                    // различим как интерактив независимо от цвета (WCAG 1.4.1, не only-color;
                    // отделяет «кликабельно» от danger-семантики статуса).
                    style={{
                      color: 'var(--danger, #dc2626)',
                      textDecoration: 'underline dotted',
                      textUnderlineOffset: '2px',
                    }}
                  >
                    ⚠ {m.project_tasks_overdue_n({ count: p.task_summary.overdue })} →
                  </Link>
                </>
              )}
            </span>
          </Field>
        )}
        {p.notes && <Field label={m.project_form_notes()}>{p.notes}</Field>}
      </div>

      {/* FE-13 / BE-1 (Sprint 2 rework): Activity feed Bento Level 1.
          UNION 4 sources — last 3 events с relative time. */}
      <ProjectActivityCard projectId={p.id} limit={3} />

      {/* FE-14 / BE-2 (Sprint 2 rework): AI Insight card — conditional render
          когда BE возвращает items. Stub возвращает [] → card hidden. */}
      <ProjectAIInsightCard projectId={p.id} />
    </div>
  );
}

// FE-7 (Sprint 2 rework): local HeroMetric extended с icon + border-left urgency
// signal. WCAG 1.4.1 — status = color + icon + text (не only-color).
// FE-162 (Sprint 2 rework follow-up from S2R-DD-3): `borderOnly` prop —
// Calm UI single-signal: border-left semantic + neutral text. Без флага text
// тоже окрашивается (legacy behavior для других callsites).
// FE-9 (Sprint 2 rework): `provenance` prop — `[staiter · 14:22]` chip under
// value. STAITER_UI §7 — каждая Hero metric имеет provenance attribution.
function HeroMetric(props: {
  label: string;
  value: string;
  /** D4.10 — ReactNode, а не строка: подпись знаменателя содержит ссылку на его состав. */
  subtle: ReactNode;
  color?: 'danger' | 'warn' | undefined;
  icon?: import('lucide-react').LucideIcon;
  borderOnly?: boolean;
  provenance?: string;
  /**
   * Путь от числа к правилу, которым оно посчитано (Evidence-first). Без него
   * Hero-метрика — тупик: процент виден, а чем он посчитан и где это менять,
   * пользователь узнать не мог.
   */
  action?: { label: string; to: string };
  /** Формула метода — правило рядом с числом, до всякого перехода. */
  methodHint?: string;
  /**
   * Третий проход гейта фазы C — БЕЗ этого объявления проп молча терялся.
   *
   * `.heroSpanTwo` был написан, попадал в JSX-спред и не доезжал до DOM: у
   * локального компонента `className` не был объявлен, а спред не даёт
   * excess-property-check. Правка «в коде есть» при «на экране нет» — ровно тот
   * класс, ради которого гейт и смотрит пиксели, а не диффы.
   */
  className?: string | undefined;
}): JSX.Element {
  const valueColor =
    props.borderOnly || !props.color
      ? 'var(--text-primary)'
      : props.color === 'danger'
        ? 'var(--danger, #dc2626)'
        : 'var(--warn, #d97706)';
  const borderLeft =
    props.color === 'danger'
      ? '3px solid var(--danger, #dc2626)'
      : props.color === 'warn'
        ? '3px solid var(--warn, #d97706)'
        : '1px solid var(--border)';
  const Icon = props.icon;
  return (
    <div
      className={props.className}
      style={{
        padding: 16,
        border: '1px solid var(--border)',
        borderLeft,
        borderRadius: 8,
        background: 'var(--bg-elevated)',
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-muted)',
          marginBottom: 4,
        }}
      >
        {Icon && <Icon size={12} aria-hidden="true" style={{ opacity: 0.7 }} />}
        {props.label}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 600,
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          color: valueColor,
        }}
      >
        {props.value}
      </div>
      {props.subtle && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{props.subtle}</div>
      )}
      {props.methodHint && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
          {props.methodHint}
        </div>
      )}
      {props.action && (
        // Rework дизайн-гейта D4: подчёркивание на ВСЕХ текстовых ссылках карточки.
        // Стрелка «→» здесь означала переход, а у соседней ссылки среза — тоже
        // переход, но подчёркнута была только одна: один смысл, два вида.
        <Link
          to={props.action.to}
          style={{
            display: 'inline-block',
            fontSize: 12,
            marginTop: 6,
            textDecoration: 'underline dotted',
            textUnderlineOffset: '2px',
          }}
        >
          {props.action.label} →
        </Link>
      )}
      {props.provenance && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-muted)',
            marginTop: 6,
            padding: '2px 6px',
            border: '1px solid var(--border)',
            borderRadius: 3,
          }}
          title="Источник: STAITER"
        >
          {props.provenance}
        </div>
      )}
    </div>
  );
}

function StatusJournalTab(props: {
  project: Awaited<ReturnType<typeof fetchProject>>;
}): JSX.Element {
  const updates = props.project.status_updates ?? [];
  // FE-86 (Sprint 10) — lifecycle events (BE-19): stage transitions, financial
  // baseline updates, status updates, project create/update.
  const lifecycleQuery = useQuery({
    queryKey: ['project-status-journal', props.project.id],
    queryFn: () => fetchProjectStatusJournal(props.project.id, { limit: 50 }),
    staleTime: 30_000,
  });
  const lifecycleItems = lifecycleQuery.data?.items ?? [];
  if (updates.length === 0 && lifecycleItems.length === 0) {
    return <div style={{ opacity: 0.6 }}>{m.audit_tab_empty()}</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {updates.length > 0 && (
        <section>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {m.ui_health_obnovleniya_pm()}
          </h4>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {updates.map((u) => (
              <li
                key={u.id}
                style={{ padding: 8, borderBottom: '1px solid var(--border, #e5e7eb)' }}
              >
                <div style={{ fontSize: 12, opacity: 0.6 }}>
                  {u.update_date.slice(0, 16).replace('T', ' ')} • {u.user_name ?? u.user_id}
                </div>
                <div>
                  {u.previous_health ?? '—'} → <strong>{u.new_health}</strong>
                </div>
                <div>{u.narrative}</div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {lifecycleItems.length > 0 && (
        <section>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {m.ui_sobytiya_lifecycle_tier_action_actor()}
          </h4>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {lifecycleItems.map((ev) => (
              <li
                key={ev.id}
                style={{ padding: 8, borderBottom: '1px solid var(--border, #e5e7eb)' }}
              >
                <div style={{ fontSize: 12, opacity: 0.6, fontFamily: 'var(--font-mono)' }}>
                  {ev.occurred_at.slice(0, 16).replace('T', ' ')} • Tier {ev.tier} •{' '}
                  {ev.actor_role ?? 'system'}
                </div>
                <div>
                  <code style={{ fontSize: 12 }}>{ev.tool_name}</code>{' '}
                  <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>({ev.status})</span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** PC-13: unified Field typography — label muted 12px uppercase tracking, value 14px primary. */
function Field(props: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        gap: 12,
        alignItems: 'baseline',
        padding: '4px 0',
      }}
    >
      <span
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-muted)',
          fontWeight: 500,
        }}
      >
        {props.label}
      </span>
      <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>{props.children}</span>
    </div>
  );
}

/** G5.5 — Health update dialog (drawer-style modal). */
function HealthUpdateDialog(props: {
  open: boolean;
  projectId: string;
  currentHealth: string;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { open, projectId, currentHealth, onClose, onSaved } = props;
  const [newHealth, setNewHealth] = useState<ProjectHealth>(currentHealth as ProjectHealth);
  const [narrative, setNarrative] = useState('');
  const mut = useMutation({
    mutationFn: () =>
      postProjectStatusUpdate(projectId, {
        new_health: newHealth,
        narrative: narrative.trim() || '—',
      }),
    onSuccess: () => {
      setNarrative('');
      onSaved();
    },
  });
  return (
    <Dialog open={open} onClose={onClose} title={m.project_health_update_title()}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
        style={{ display: 'grid', gap: 10 }}
      >
        <Select
          label={m.project_col_health()}
          value={newHealth}
          onChange={(e) => setNewHealth(e.target.value as ProjectHealth)}
          options={Object.entries(PROJECT_HEALTH_LABEL).map(([k, v]) => ({ value: k, label: v }))}
        />
        <Textarea
          label={m.project_health_update_narrative_placeholder()}
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          placeholder={m.project_health_update_narrative_placeholder()}
          rows={4}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            {m.common_button_cancel()}
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {m.project_health_update_submit()}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
